import { useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import * as THREE from 'three';
import { useThree, type ThreeEvent } from '@react-three/fiber';
import { Line, OrbitControls, OrthographicCamera } from '@react-three/drei';
import {
  DraggableCargoBox,
  GhostBox,
} from '@/features/import-tracking/components/draggable-cargo-box';
import type {
  ContainerBounds,
  LayoutBox,
  PlacementCheck,
} from '@/features/import-tracking/utils/container-layout';
import { useChartTheme } from '@/lib/charts/use-chart-theme';

/**
 * La escena de Comercia › Carga (maqueta de Melanie): el contenedor en
 * isométrico, con el fondo, la pared del costado y el piso en lavanda, las
 * aristas marcadas (punteadas las de adelante) y la carga a escala real, una
 * caja por bulto.
 *
 * Ejes: el acomodo trabaja en cm con el alto en z; three.js en metros con el
 * alto en y (three.x = largo, three.y = alto, three.z = ancho). El fondo es
 * x = 0 y la puerta x = largo, que queda abajo a la derecha, más cerca.
 */

export interface PropsDeEscena {
  limites: ContainerBounds;
  cajas: LayoutBox[];
  colorDe: (id: string) => string;
  rotuloDe: (id: string) => string;
  seleccionada: string | null;
  arrastrando: string | null;
  fantasma: { box: LayoutBox; check: PlacementCheck } | null;
  soloLectura: boolean;
  onElegir: (id: string | null) => void;
  onEmpezarArrastre: (id: string, e: ThreeEvent<PointerEvent>) => void;
  onMoverArrastre: (xCm: number, yCm: number) => void;
  /** Para soltar productos desde la lista: convierte un punto de la pantalla en cm del piso. */
  pisoDesdePantalla: MutableRefObject<
    ((clientX: number, clientY: number) => [number, number] | null) | null
  >;
  /** Cambia cuando hay que volver a encuadrar (otro contenedor, otro tamaño). */
  encuadre: string;
}

/**
 * Los colores del contenedor, medidos sobre la captura de la maqueta. Son
 * planos (sin luces): así el fondo, el costado y el piso tienen exactamente ese
 * tono. En oscuro no hay maqueta; se mantienen las mismas diferencias.
 */
const COLORES = {
  claro: { fondo: '#E5E3EB', costado: '#ECEAF0', piso: '#EFECF4', arista: '#6E6680' },
  oscuro: { fondo: '#2E2A3B', costado: '#272433', piso: '#2A2737', arista: '#9A93B0' },
};

export function EscenaDeCarga(props: PropsDeEscena) {
  const { limites } = props;
  const L = limites.lengthCm / 100;
  const W = limites.widthCm / 100;
  const H = limites.heightCm / 100;
  const centro = useMemo(() => new THREE.Vector3(L / 2, H / 2, W / 2), [L, W, H]);

  const colisiones = new Set(props.fantasma?.check.collidesWith ?? []);

  return (
    <>
      <Camara L={L} W={W} H={H} centro={centro} encuadre={props.encuadre} />
      <OrbitControls
        makeDefault
        enabled={props.arrastrando === null}
        target={centro}
        enablePan={false}
        minPolarAngle={0.25}
        maxPolarAngle={Math.PI / 2.1}
        minZoom={8}
        maxZoom={400}
        enableDamping
        dampingFactor={0.1}
      />

      {/* Sin luces: todo va en colores planos, como la maqueta. */}
      <Contenedor L={L} W={W} H={H} onFondo={() => props.onElegir(null)} />

      {props.cajas.map((b) => (
        <DraggableCargoBox
          key={b.id}
          box={b}
          color={props.colorDe(b.id)}
          label={props.rotuloDe(b.id)}
          isSelected={props.seleccionada === b.id}
          isDragging={props.arrastrando === b.id}
          isCollisionTarget={colisiones.has(b.id)}
          isDimmed={false}
          onSelect={(id) => props.onElegir(id)}
          onDragStart={props.soloLectura ? () => undefined : props.onEmpezarArrastre}
          rotuloFijo
          plana
        />
      ))}

      {props.fantasma && <GhostBox box={props.fantasma.box} valid={props.fantasma.check.valid} />}

      <PlanoDeArrastre activo={props.arrastrando !== null} onMover={props.onMoverArrastre} />
      <ReceptorDeSoltar destino={props.pisoDesdePantalla} />
    </>
  );
}

/** Cámara ortográfica (isométrica, como la maqueta) que encuadra el contenedor entero. */
function Camara({
  L,
  W,
  H,
  centro,
  encuadre,
}: {
  L: number;
  W: number;
  H: number;
  centro: THREE.Vector3;
  encuadre: string;
}) {
  const { size, controls } = useThree();
  const camara = useRef<THREE.OrthographicCamera>(null);

  useEffect(() => {
    const c = camara.current;
    if (!c) return;
    // Isométrica de verdad, como la maqueta: desde arriba, del lado de la puerta
    // y del costado abierto, con el largo y el ancho a 30° de la horizontal.
    const direccion = new THREE.Vector3(1, 1, 1).normalize();
    const distancia = Math.max(L, W, H) * 3;
    c.position.copy(centro.clone().add(direccion.multiplyScalar(distancia)));
    c.lookAt(centro);
    // El zoom que hace entrar el contenedor proyectado, con margen.
    const esquinas = [0, L].flatMap((x) =>
      [0, H].flatMap((y) => [0, W].map((z) => new THREE.Vector3(x, y, z)))
    );
    c.updateMatrixWorld();
    const vista = c.matrixWorldInverse;
    const xs: number[] = [];
    const ys: number[] = [];
    for (const e of esquinas) {
      const v = e.clone().applyMatrix4(vista);
      xs.push(v.x);
      ys.push(v.y);
    }
    const ancho = Math.max(...xs) - Math.min(...xs);
    const alto = Math.max(...ys) - Math.min(...ys);
    c.zoom = Math.min(size.width / (ancho * 1.12), size.height / (alto * 1.2));
    c.updateProjectionMatrix();
    const orbita = controls as unknown as { target?: THREE.Vector3; update?: () => void } | null;
    if (orbita?.target) {
      orbita.target.copy(centro);
      orbita.update?.();
    }
    // Se reencuadra al cambiar de contenedor o de tamaño de la ventana.
  }, [L, W, H, centro, size.width, size.height, encuadre, controls]);

  return <OrthographicCamera ref={camara} makeDefault near={-200} far={400} />;
}

/** El contenedor: piso, fondo y costado en lavanda; las aristas, punteadas las de adelante. */
function Contenedor({
  L,
  W,
  H,
  onFondo,
}: {
  L: number;
  W: number;
  H: number;
  onFondo: () => void;
}) {
  const color = COLORES[useChartTheme().oscuro ? 'oscuro' : 'claro'];
  const p = (x: number, y: number, z: number) => [x, y, z] as [number, number, number];
  /*
    Punteadas, las tres aristas de la esquina de arriba más cercana (la del
    techo junto a la puerta), que pasan por delante de la carga: como en la
    maqueta, se ve que están adelante sin tapar lo que hay detrás.
  */
  const solidas: Array<[[number, number, number], [number, number, number]]> = [
    [p(0, 0, 0), p(L, 0, 0)],
    [p(0, 0, 0), p(0, 0, W)],
    [p(0, 0, W), p(L, 0, W)],
    [p(L, 0, 0), p(L, 0, W)],
    [p(0, 0, 0), p(0, H, 0)],
    [p(L, 0, 0), p(L, H, 0)],
    [p(0, 0, W), p(0, H, W)],
    [p(0, H, 0), p(L, H, 0)],
    [p(0, H, 0), p(0, H, W)],
  ];
  const punteadas: Array<[[number, number, number], [number, number, number]]> = [
    [p(0, H, W), p(L, H, W)],
    [p(L, H, 0), p(L, H, W)],
    [p(L, 0, W), p(L, H, W)],
  ];
  return (
    <group>
      {/* Piso */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[L / 2, -0.002, W / 2]}
        onPointerDown={(e) => {
          e.stopPropagation();
          onFondo();
        }}
      >
        <planeGeometry args={[L, W]} />
        <meshBasicMaterial color={color.piso} />
      </mesh>
      {/* Fondo (x = 0) */}
      <mesh rotation={[0, Math.PI / 2, 0]} position={[-0.002, H / 2, W / 2]}>
        <planeGeometry args={[W, H]} />
        <meshBasicMaterial color={color.fondo} side={THREE.DoubleSide} />
      </mesh>
      {/* Costado (z = 0) */}
      <mesh position={[L / 2, H / 2, -0.002]}>
        <planeGeometry args={[L, H]} />
        <meshBasicMaterial color={color.costado} side={THREE.DoubleSide} />
      </mesh>
      {solidas.map(([a, b], i) => (
        <Line key={`s${i}`} points={[a, b]} color={color.arista} lineWidth={1.75} />
      ))}
      {punteadas.map(([a, b], i) => (
        <Line
          key={`d${i}`}
          points={[a, b]}
          color={color.arista}
          lineWidth={1.5}
          dashed
          dashSize={0.11}
          gapSize={0.08}
        />
      ))}
    </group>
  );
}

/**
 * Plano invisible sobre el piso que sigue al puntero mientras se arrastra: sin
 * él no hay contra qué proyectar el mouse. Devuelve centímetros del acomodo.
 */
function PlanoDeArrastre({
  activo,
  onMover,
}: {
  activo: boolean;
  onMover: (xCm: number, yCm: number) => void;
}) {
  const { camera, raycaster, pointer } = useThree();
  const plano = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0));
  const punto = useRef(new THREE.Vector3());

  // Sólo el movimiento: el soltar lo escucha el editor desde que se agarra la caja.
  useEffect(() => {
    if (!activo) return;
    const mover = () => {
      raycaster.setFromCamera(pointer, camera);
      if (raycaster.ray.intersectPlane(plano.current, punto.current)) {
        onMover(punto.current.x * 100, punto.current.z * 100);
      }
    };
    window.addEventListener('pointermove', mover);
    return () => window.removeEventListener('pointermove', mover);
  }, [activo, camera, raycaster, pointer, onMover]);

  return null;
}

/** Registra cómo pasar de un punto de la pantalla al piso del contenedor (para soltar desde la lista). */
function ReceptorDeSoltar({ destino }: { destino: PropsDeEscena['pisoDesdePantalla'] }) {
  const { camera, gl } = useThree();
  useEffect(() => {
    const raycaster = new THREE.Raycaster();
    const plano = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const punto = new THREE.Vector3();
    destino.current = (clientX, clientY) => {
      const r = gl.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((clientX - r.left) / r.width) * 2 - 1,
        -((clientY - r.top) / r.height) * 2 + 1
      );
      raycaster.setFromCamera(ndc, camera);
      return raycaster.ray.intersectPlane(plano, punto) ? [punto.x * 100, punto.z * 100] : null;
    };
    return () => {
      destino.current = null;
    };
  }, [camera, gl, destino]);
  return null;
}
